<script setup>
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/vue";
import {
    MinusSmallIcon,
    PlusSmallIcon,
} from "@heroicons/vue/24/outline/index.js";

const props = defineProps({
    appendedFaqs: {
        type: Array,
        default: [],
    },
    // faqs supersede defaultFaqs
    faqs: {
        type: Array,
        default: [],
    },
});

const defaultFaqs = [
    {
        question: "What is a Guide?",
        answer: "A Guide is a collection of prompts and questions that tells Qualta AI how to conduct the interview and what topics to cover. This is similar to a written guide or plan a human research interviewer would use.",
    },
    {
        question: "Is Qualta secure?",
        answer: 'Yes, Qualta is very secure.<br/><br/>All interview data on Qualta is encrypted:<ul class="list-disc list-inside"><li>In transit, using TLS (Transport Layer Security), so that information sent between your device and our servers is fully secured.</li><li>At rest, using AES‑256 encryption – the same encryption standard used in banking, healthcare, and government systems.</li></ul><br/>Qualta keeps your data protected:<br/><ul class="list-disc list-inside"><li>We do not use your prompts or completions to train any models, ever.</li><li>We do not share logs, prompts, or completions with any AI model providers.</li></ul><br/>With Qualta, Interview responses are confidential and can only be accessed by assigned team members. Administrators can manage roles and maintain control over who can view research content.',
    },
    {
        question: "What counts as an interview?",
        answer: "Only completed interviews, where the respondent answers all questions presented (not including demographic questions), are counted against your quota. You will not be charged for partial interviews in any way nor will you have access to them. Partial interviews are deleted from Qualta on a regular basis.",
    },
    {
        question: "How do you prevent abuse?",
        answer: "Respondents must pass a security scan to prove they are human. We also use a digital fingerprint to prevent them from completing the same interview more than once.",
    },
    {
        question: "What if I need more interviews?",
        answer: 'You can add interviews to your plan at any time in Team settings. See <a href="/info/pricing" class="underline text-brand-dark dark:text-brand-light">plan pricing</a> for details.',
    },
    // {
    //   question: "",
    //   answer: "",
    // },
];

const faqs =
    props.faqs.length === 0
        ? defaultFaqs.concat(props.appendedFaqs)
        : props.faqs.concat(props.appendedFaqs);
</script>

<template>
    <div class="mx-auto mt-32 max-w-7xl px-6 sm:mt-56 lg:px-8">
        <div
            class="mx-auto max-w-4xl divide-y divide-gray-900/10 dark:divide-gray-800"
        >
            <h2
                class="pb-10 text-4xl font-semibold tracking-tight text-gray-900 dark:text-gray-100 sm:text-5xl"
            >
                Frequently asked questions
            </h2>
            <dl class="divide-y divide-gray-900/10 dark:divide-gray-800">
                <Disclosure
                    as="div"
                    v-for="faq in faqs"
                    :key="faq.question"
                    class="py-6 flex flex-col gap-3"
                    v-slot="{ open }"
                >
                    <dt>
                        <DisclosureButton
                            class="flex w-full items-start justify-between text-left text-gray-900 dark:text-gray-100 cursor-pointer"
                        >
                            <span class="text-base/7 font-semibold">{{
                                faq.question
                            }}</span>
                            <span class="ml-6 flex h-7 items-center">
                                <PlusSmallIcon
                                    v-if="!open"
                                    class="size-6"
                                    aria-hidden="true"
                                />
                                <MinusSmallIcon
                                    v-else
                                    class="size-6"
                                    aria-hidden="true"
                                />
                            </span>
                        </DisclosureButton>
                    </dt>
                    <DisclosurePanel as="dd" class="pr-12">
                        <p
                            class="text-base/7 text-gray-600 dark:text-gray-300"
                            v-html="faq.answer"
                        ></p>
                    </DisclosurePanel>
                </Disclosure>
            </dl>
        </div>
    </div>
</template>
